// Shared types + sessionStorage helpers for an AI scan in flight.
// We intentionally keep the photo + AI result off the URL and out of the DB
// until the user actually views /result (where Result.tsx persists it).

import { FIG_ITEM } from "@/data/fig";

export interface AIPlatform {
  name: string;
  price: number;
  note: string;
  fee: number;
  net: number;
  hot?: boolean;
}

export interface AIScan {
  title: string;
  brand: string;
  era: string;
  origin: string;
  category: string;
  material: string;
  size: string;
  color: string;
  condition: string;
  condition_notes: string;
  verdict: "PASS" | "FAIL";
  verdict_label: string;
  confidence: number;
  rarity: string;
  tags: string[];
  notes: string[];
  forensic: { label: string; value: string; confidence: number }[];
  price_low: number;
  price_suggested: number;
  price_high: number;
  platforms?: AIPlatform[];
  // How many of the user's uploaded photos Fig actually analyzed (1–5).
  // Set by the merged-verdict edge function.
  analyzed_count?: number;
  // Verification block — only present when the AI flagged this garment as
  // verification-sensitive (fur, leather, luxury, exotic). Drives hedged
  // listing language and the evidence-photo prompt on /result.
  verification?: {
    category_flag: "fur" | "leather" | "luxury" | "exotic";
    tier: "photo_verified" | "self_reported" | "uncertain";
    evidence: string[];
    prompts: string[];
  } | null;
  // Set by the edge function — true when at least one photo clearly showed
  // a primary brand label. The "label proof" rule on the server uses this
  // together with `hint_conflict` to block PASS verdicts that contradict
  // the user's hint without label evidence.
  main_label_clear?: boolean;
  hint_conflict?: { conflicts: boolean; reason: string };
  pricing_comparable_count?: number;
  pricing_data_source?: "serpapi" | "dataforseo" | "market" | "none";
}

const PHOTO_KEY = "fig_scan_photo";
const ANALYZE_IMAGES_KEY = "fig_scan_analyze_images"; // data URLs for the AI (multi)
const PHOTOS_KEY = "fig_scan_photo_urls";  // uploaded public URLs (multi)
const RESULT_KEY = "fig_scan_ai_result";
const ERROR_KEY = "fig_scan_ai_error";
const HINTS_KEY = "fig_scan_user_hints";   // optional user-provided extra details
const VERIFY_KEY = "fig_verify_result";    // raw FIG Verify edge function payload
const CATEGORY_KEY = "fig_scan_category";  // user-selected garment category
const PHOTO_LABELS_KEY = "fig_scan_photo_labels"; // ordered slot labels
const BRAND_KEY = "fig_scan_user_brand";          // user-provided brand name

export interface FigVerifyPricing {
  guidance: {
    market_range: string;
    recommended_range: string;
    condition_note: string;
    pricing_rationale: string;
    based_on: string;
    confidence: string;
  } | null;
  sources: string[];
  platform_recommendations: Array<{ platform: string; rationale: string }>;
  comparable_count: number;
  data_source: "serpapi" | "dataforseo" | "market" | "none";
  listings_preview: Array<{
    title: string;
    price: number;
    platform: string;
    url: string;
    isSold: boolean;
  }>;
  item_searched: string;
  condition_tier: string;
}

export interface FigVerifyResult {
  item?: { estimated_era?: string; brand_detected?: string | null; detected_type?: string };
  scores?: Record<string, number | null | string>;
  assessment?: { overall_confidence?: string; verdict?: string };
  pricing?: FigVerifyPricing | null;
  // v2 fields from the reference-doc-driven Edge Function. Optional so
  // pre-v2 cached results in sessionStorage don't break the typing.
  reference_doc_version?: string;
  internal_verdict?: "Consistent" | "Inconsistent" | "Insufficient";
  internal_confidence?: "High" | "Medium" | "Low";
  category_advisory?: string | null;
  low_confidence_caveat?: string | null;
  signals_observed?: Array<{
    feature: string;
    image_index: number;
    ref_section: string;
    tag: "supports" | "contradicts" | "inconclusive" | "missing";
    /** One-sentence educational note: connects what was observed to why it
     * matters for the verdict (e.g. "Care labels became mandatory in the US
     * in July 1972, so a polyester care tag rules out pre-1971 production").
     * Optional so older results without rationale still render. */
    rationale?: string;
  }>;
  anachronisms?: string[];
  missing_signals?: string[];
  /** Short educational paragraph about the claimed era — what defined it
   * stylistically, materially, and in label conventions. Surfaced as a
   * "Learn more about this era" card on the result page. */
  era_context?: string;
  /** Short educational paragraph about the claimed brand — relevant history,
   * label evolution, era-specific construction tells. */
  brand_context?: string;
  requested_lookups?: Array<{ topic: string; query: string; reason: string }>;
  [k: string]: unknown;
}

export const scanStore = {
  setPhoto(dataUrl: string) {
    try { sessionStorage.setItem(PHOTO_KEY, dataUrl); } catch { /* quota */ }
  },
  getPhoto(): string | null {
    return sessionStorage.getItem(PHOTO_KEY);
  },
  clearPhoto() { sessionStorage.removeItem(PHOTO_KEY); },

  // Per-photo data URLs Fig will analyze. Sized down on the client. Capped at
  // ~5 entries via MAX_PHOTOS, but we still guard against sessionStorage quota
  // (data URLs of resized JPEGs are usually 200–500KB each).
  setAnalyzeImages(dataUrls: string[]) {
    try { sessionStorage.setItem(ANALYZE_IMAGES_KEY, JSON.stringify(dataUrls)); }
    catch { /* quota — fall back to single-photo flow already covered by setPhoto */ }
  },
  getAnalyzeImages(): string[] {
    const raw = sessionStorage.getItem(ANALYZE_IMAGES_KEY);
    if (!raw) return [];
    try { return JSON.parse(raw) as string[]; } catch { return []; }
  },
  clearAnalyzeImages() { sessionStorage.removeItem(ANALYZE_IMAGES_KEY); },

  // Uploaded public photo URLs (1–5 garment photos).
  setPhotos(urls: string[]) {
    try { sessionStorage.setItem(PHOTOS_KEY, JSON.stringify(urls)); } catch { /* quota */ }
  },
  getPhotos(): string[] {
    const raw = sessionStorage.getItem(PHOTOS_KEY);
    if (!raw) return [];
    try { return JSON.parse(raw) as string[]; } catch { return []; }
  },
  clearPhotos() { sessionStorage.removeItem(PHOTOS_KEY); },

  setResult(r: AIScan) {
    sessionStorage.setItem(RESULT_KEY, JSON.stringify(r));
    sessionStorage.removeItem(ERROR_KEY);
  },
  getResult(): AIScan | null {
    const raw = sessionStorage.getItem(RESULT_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as AIScan; } catch { return null; }
  },
  clearResult() {
    sessionStorage.removeItem(RESULT_KEY);
    sessionStorage.removeItem("fig_scan_saved_id");
  },

  setError(msg: string) { sessionStorage.setItem(ERROR_KEY, msg); },
  getError(): string | null { return sessionStorage.getItem(ERROR_KEY); },
  clearError() { sessionStorage.removeItem(ERROR_KEY); },

  // Optional free-text hints the user types on /scan (brand they spotted,
  // "real fur", "smells musty", etc.) — fed to the AI alongside the photo.
  setHints(text: string) {
    const trimmed = text.trim();
    if (trimmed) sessionStorage.setItem(HINTS_KEY, trimmed);
    else sessionStorage.removeItem(HINTS_KEY);
  },
  getHints(): string | null { return sessionStorage.getItem(HINTS_KEY); },
  clearHints() { sessionStorage.removeItem(HINTS_KEY); },

  setCategory(c: string) {
    if (c) sessionStorage.setItem(CATEGORY_KEY, c);
    else sessionStorage.removeItem(CATEGORY_KEY);
  },
  getCategory(): string | null { return sessionStorage.getItem(CATEGORY_KEY); },
  clearCategory() { sessionStorage.removeItem(CATEGORY_KEY); },

  setBrand(brand: string) {
    const trimmed = brand.trim();
    if (trimmed) sessionStorage.setItem(BRAND_KEY, trimmed);
    else sessionStorage.removeItem(BRAND_KEY);
  },
  getBrand(): string | null { return sessionStorage.getItem(BRAND_KEY); },
  clearBrand() { sessionStorage.removeItem(BRAND_KEY); },

  setPhotoLabels(labels: string[]) {
    try { sessionStorage.setItem(PHOTO_LABELS_KEY, JSON.stringify(labels)); } catch { /* quota */ }
  },
  getPhotoLabels(): string[] {
    const raw = sessionStorage.getItem(PHOTO_LABELS_KEY);
    if (!raw) return [];
    try { return JSON.parse(raw) as string[]; } catch { return []; }
  },
  clearPhotoLabels() { sessionStorage.removeItem(PHOTO_LABELS_KEY); },

  // Raw FIG Verify edge function response (includes pricing.guidance).
  setVerifyResult(r: FigVerifyResult) {
    try { sessionStorage.setItem(VERIFY_KEY, JSON.stringify(r)); } catch { /* quota */ }
  },
  getVerifyResult(): FigVerifyResult | null {
    const raw = sessionStorage.getItem(VERIFY_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as FigVerifyResult; } catch { return null; }
  },
  clearVerifyResult() { sessionStorage.removeItem(VERIFY_KEY); },
};

// Map the AI result into the FIG_ITEM-shaped object Result.tsx already renders.
// Keeps the existing pricing/comps demo data so Price.tsx still works.
export function mergedItem(ai: AIScan | null) {
  if (!ai) return FIG_ITEM;
  return {
    ...FIG_ITEM,
    title: ai.title,
    brand: ai.brand,
    era: ai.era,
    origin: ai.origin,
    category: ai.category,
    material: ai.material,
    size: ai.size,
    color: ai.color,
    condition: ai.condition,
    conditionNotes: ai.condition_notes,
    verdict: ai.verdict,
    verdictLabel: ai.verdict_label,
    confidence: ai.confidence,
    rarity: ai.rarity,
    tags: ai.tags,
    notes: ai.notes,
    forensic: ai.forensic,
    analyzedCount: ai.analyzed_count ?? 1,
    verification: ai.verification ?? null,
    pricing: {
      ...FIG_ITEM.pricing,
      low: ai.price_low ?? FIG_ITEM.pricing.low,
      suggested: ai.price_suggested ?? FIG_ITEM.pricing.suggested,
      high: ai.price_high ?? FIG_ITEM.pricing.high,
      // Use AI per-platform pricing when present; fall back to FIG defaults.
      platforms: (ai.platforms && ai.platforms.length > 0)
        ? ai.platforms.map((p) => ({
            name: p.name,
            price: p.price,
            note: p.note,
            fee: p.fee,
            net: p.net,
            hot: !!p.hot,
          }))
        : FIG_ITEM.pricing.platforms,
    },
  };
}
